import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getProducts, getCategories } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";
import { catVisible } from "../lib/format";
import { SlidersHorizontal } from "lucide-react";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const search = params.get("buscar") || "";
  const category = params.get("categoria") || "";
  const sort = params.get("orden") || "name";
  const [data, setData] = useState({ items: [], total: 0 });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCategories().then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getProducts({ search, category, sort, limit: 100 })
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, category, sort]);

  const update = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val);
    else p.delete(key);
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Seo
        title="Tienda online de equipamiento para panadería"
        description="Catálogo completo de equipamiento de panadería FABRIPAN: bandejas, armarios de transporte, moldes, carros bandejeros y accesorios. Fabricación bajo pedido, directo de fábrica a su panadería."
        keywords="tienda equipamiento panadería, comprar bandejas panadería, catálogo maquinaria panadería, suministros panadería online"
      />
      <div className="mb-6">
        <h1 className="font-heading font-bold text-4xl tracking-tighter">Tienda</h1>
        <p className="text-muted-foreground mt-1">
          {search ? `Resultados para "${search}"` : "Todo el catálogo profesional de FABRIPAN"} · {data.total} productos
        </p>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-6">
          <div className="border border-border rounded-sm bg-white p-4">
            <div className="flex items-center gap-2 mb-3 font-heading font-semibold">
              <SlidersHorizontal className="h-4 w-4 text-brand" /> Categorías
            </div>
            <ul className="space-y-1 text-xs max-h-96 overflow-y-auto no-scrollbar">
              <li>
                <button onClick={() => update("categoria", "")} className={`text-left w-full py-1 text-xs leading-tight hover:text-brand transition-colors ${!category ? "text-brand font-semibold" : "text-muted-foreground"}`} data-testid="filter-cat-all">
                  Todas
                </button>
              </li>
              {cats.filter((c) => catVisible(c, cats)).map((c) => (
                <li key={c.id} className={c.parent_id ? "pl-3" : ""}>
                  <button onClick={() => update("categoria", c.slug)} className={`text-left w-full py-1 text-xs leading-tight uppercase break-words hover:text-brand transition-colors ${category === c.slug ? "text-brand font-semibold" : "text-black"}`} data-testid={`filter-cat-${c.slug}`}>
                    {c.name} <span className="text-[10px] opacity-60 normal-case">({c.product_count})</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-muted-foreground">{data.items.length} productos</span>
            <select value={sort} onChange={(e) => update("orden", e.target.value)} className="text-sm border border-border rounded-sm px-3 py-2 bg-white" data-testid="sort-select">
              <option value="name">Nombre A-Z</option>
              <option value="price_asc">Precio ascendente</option>
              <option value="price_desc">Precio descendente</option>
            </select>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-secondary animate-pulse rounded-sm" />)}
            </div>
          ) : data.items.length === 0 ? (
            <p className="text-muted-foreground py-20 text-center">No se encontraron productos.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="shop-grid">
              {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
